import { Play } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { cn } from 'lib/utils';
import { ColorRange } from 'services/face-scan/type';
import {
  CategorizedReadingData,
  ReadingData,
  ReadingDataWithSubParameters,
} from '../../types';
import Icon from '../icon/Icon';

interface ReportHealthReadingProps {
  data: CategorizedReadingData;
  className?: string;
  onVitalClick?: (
    index: number,
    parameter: string,
    colorRanges: ColorRange[]
  ) => void;
}

export default function ReportHealthReading({
  data,
}: ReportHealthReadingProps) {
  const { t } = useTranslation();

  if (!Object.keys(data)?.length) {
    return (
      <div className='border-secondary max-lg:border rounded-xl h-40 lg:h-[calc(100vh-13rem)] grid place-items-center font-medium'>
        <p>{t('no_scan_data')}</p>
      </div>
    );
  }

  return (
    <div className='flex flex-col space-y-8'>
      {Object.keys(data)?.map((category) => {
        return (
          <div key={category}>
            <h2 className='text-xl font-semibold'>{category}</h2>

            {data[category]?.length ? (
              <div className='p-4 mt-2 ml-2 border-l-2 border-l-primary'>
                {data[category]?.map((vital) => (
                  <ScanVitalDetail vital={vital} key={vital.key} />
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function ScanVitalDetail({ vital }: { vital: ReadingDataWithSubParameters }) {
  const renderColorRange = (vital: ReadingData | undefined, name?: string) => {
    if (!vital) return null;

    return (
      <div
        className='relative grid items-end w-full mt-2 lg:mt-0 lg:w-2/3'
        style={{
          gridTemplateColumns: `repeat(${vital?.colorRange?.length}, minmax(0, 1fr))`,
        }}
      >
        {vital?.colorRange?.map(({ color, range }, index: number) => (
          <div key={index} className='relative col-span-1'>
            <div
              style={{ backgroundColor: color }}
              className={cn('w-full h-2', range === vital.valueRange && 'h-4')}
            />
            {range === vital.valueRange && (
              <>
                <div
                  className={cn(
                    'absolute flex flex-col items-center -translate-x-1/2 left-1/2',
                    name ? '-top-7' : '-top-4'
                  )}
                >
                  {name ? (
                    <p className='-mb-1 text-sm leading-none'>{name}</p>
                  ) : null}
                  <Play
                    className='w-4 h-4 rotate-90'
                    style={{
                      color: vital.color,
                      fill: vital.color,
                    }}
                  />
                </div>
                <p
                  style={{ color }}
                  className='absolute text-sm font-semibold -translate-x-1/2 -bottom-5 left-1/2 text-nowrap'
                >
                  {range[0]} {range[1] ? `- ${range[1]}` : ''}
                </p>
              </>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderIcon = (key: string, value: string | number, color: string) => (
    <Icon
      icon={key}
      size={28}
      color={value === 'Unknown' ? '#808080' : color}
    />
  );

  const isBloodPressure = vital.key === 'BLOOD_PRESSURE';

  return (
    <div className='py-4 transition-colors duration-300 cursor-default border-y border-y-primary'>
      <div
        className={cn(
          'grid items-center justify-between w-full grid-cols-7 gap-y-4'
        )}
      >
        <div className='flex items-center col-span-7 gap-2 lg:col-span-3'>
          {renderIcon(vital.key, vital.value, vital.color)}

          <div className='font-medium leading-none'>
            <h2>{vital?.display || vital.name}</h2>
            {isBloodPressure ? (
              <p className='font-semibold'>
                {vital?.categories?.systolic?.value}/
                {vital?.categories?.diastolic?.value}{' '}
                {vital?.categories?.systolic?.unit}
              </p>
            ) : (
              <p className='font-semibold'>
                {vital.value} {vital?.unit}
              </p>
            )}
          </div>
        </div>

        {isBloodPressure ? (
          <div className='flex flex-col items-center mt-4 mb-2 space-y-12 lg:items-end col-span-full lg:col-span-4'>
            {renderColorRange(vital?.categories?.systolic, 'systolic')}
            {renderColorRange(vital?.categories?.diastolic, 'diastolic')}
          </div>
        ) : (
          <div className='flex justify-center mb-2 lg:mb-0 lg:justify-end col-span-full lg:col-span-4'>
            {renderColorRange(vital)}
          </div>
        )}
      </div>

      {vital?.subParameters?.length ? (
        <Accordion type='single' collapsible>
          <AccordionItem value='item-1'>
            <AccordionTrigger className='px-4 py-1 mt-4 text-sm border rounded-sm text-primary border-primary'>
              Sub Parameters
            </AccordionTrigger>
            <AccordionContent>
              <div className='flex flex-col mt-4 space-y-5 lg:mx-6'>
                {vital.subParameters.map((subparam) => (
                  <div
                    key={subparam.key}
                    className='grid items-center justify-between w-full grid-cols-7 gap-y-4'
                  >
                    <div className='flex items-center col-span-7 gap-2 lg:col-span-3'>
                      {renderIcon(subparam.key, subparam.value, subparam.color)}

                      <div className='font-medium leading-none'>
                        <h2>{subparam.name}</h2>
                        <p className='font-semibold'>
                          {subparam.value} {subparam?.unit}
                        </p>
                      </div>
                    </div>

                    <div className='flex justify-center mb-2 lg:mb-0 lg:justify-end col-span-full lg:col-span-4'>
                      {renderColorRange(subparam)}
                    </div>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      ) : null}
    </div>
  );
}
